import * as THREE from "three";
import { getViewerHandles } from "./viewerHandles";

type CameraHomeSnapshot = {
  position: [number, number, number];
  target: [number, number, number];
  up: [number, number, number];
  quaternion: [number, number, number, number];
  fov: number;
  zoom: number;
  near: number;
  far: number;
};

let cameraHomeSnapshot: CameraHomeSnapshot | null = null;
let activeAnimationFrame: number | null = null;

const toTuple3 = (vector: THREE.Vector3): [number, number, number] => [
  vector.x,
  vector.y,
  vector.z,
];

const toTuple4 = (quaternion: THREE.Quaternion): [number, number, number, number] => [
  quaternion.x,
  quaternion.y,
  quaternion.z,
  quaternion.w,
];

export function captureCameraHome(): boolean {
  const { camera, controls } = getViewerHandles();
  if (!camera || !controls?.target) return false;

  cameraHomeSnapshot = {
    position: toTuple3(camera.position),
    target: toTuple3(controls.target),
    up: toTuple3(camera.up),
    quaternion: toTuple4(camera.quaternion),
    fov: Number(camera.fov) || 75,
    zoom: Number(camera.zoom) || 1,
    near: Number(camera.near) || 0.02,
    far: Number(camera.far) || 1000,
  };
  return true;
}

export function hasCameraHome(): boolean {
  return cameraHomeSnapshot !== null;
}

export function restoreCameraHome(durationMs = 360): boolean {
  const { camera, controls } = getViewerHandles();
  const snapshot = cameraHomeSnapshot;
  if (!snapshot || !camera || !controls?.target) return false;

  if (activeAnimationFrame) {
    cancelAnimationFrame(activeAnimationFrame);
    activeAnimationFrame = null;
  }

  const startPosition = camera.position.clone();
  const startTarget = controls.target.clone();
  const startUp = camera.up.clone();
  const startQuaternion = camera.quaternion.clone();
  const endPosition = new THREE.Vector3(...snapshot.position);
  const endTarget = new THREE.Vector3(...snapshot.target);
  const endUp = new THREE.Vector3(...snapshot.up).normalize();
  const endQuaternion = new THREE.Quaternion(...snapshot.quaternion).normalize();
  const startTime = Date.now();

  camera.fov = snapshot.fov;
  camera.zoom = snapshot.zoom;
  camera.near = snapshot.near;
  camera.far = snapshot.far;
  camera.updateProjectionMatrix?.();

  if (durationMs <= 0) {
    camera.position.copy(endPosition);
    camera.up.copy(endUp);
    camera.quaternion.copy(endQuaternion);
    controls.target.copy(endTarget);
    controls.update?.();
    return true;
  }

  const step = () => {
    const progress = Math.min((Date.now() - startTime) / durationMs, 1);
    const ease = -(Math.cos(Math.PI * progress) - 1) / 2;
    camera.position.lerpVectors(startPosition, endPosition, ease);
    camera.up.lerpVectors(startUp, endUp, ease).normalize();
    camera.quaternion.slerpQuaternions(startQuaternion, endQuaternion, ease);
    controls.target.lerpVectors(startTarget, endTarget, ease);
    controls.update?.();
    if (progress < 1) {
      activeAnimationFrame = requestAnimationFrame(step);
    } else {
      activeAnimationFrame = null;
    }
  };

  activeAnimationFrame = requestAnimationFrame(step);
  return true;
}
